import React from "react";
import { produce } from "immer";
import {
  SSOResponse,
  SingleSignOnViewModel,
  StudentPersonalInfoViewModel,
  VerificationService,
} from "../lib/types/types";
import {
  VerificationStepsEnum,
  getRouteOverride,
  closeTabRef,
  logger,
  setTabRef,
  getTabRef,
  LogoComponent,
} from "../es6";
import { FormFooterComponent as FormFooter } from "./FormFooter/FormFooterComponent";
import { dashed } from "../lib/utils/strings";
import { SingleSignOnPendingComponent } from "./StepPending/SingleSignOnPendingComponent";
import { EventName, recordCTAClickEvent } from "../lib/GoogleAnalytics/ga";
import { SSOContentComponent } from "./StepSSO/SSOContentComponent";
import { ReclaimContentComponent } from "./StepSSO/ReclaimContentComponent";

export type SingleSignOnMessages = {
  title: React.ReactNode;
  subtitle: React.ReactNode;
  login: React.ReactNode;
  cancel: React.ReactNode;
  subtitle2?: React.ReactNode;
  legal?: React.ReactNode;
};

export enum ButtonType {
  PRIMARY = "primary",
  SECONDARY = "secondary",
}

export interface SSOContentProps {
  verificationService: VerificationService;
  messages: SingleSignOnMessages;
  updateViewModelAndOpenLoginTab: (key: string, value: boolean) => void;
  dashedStepName: string;
}

interface SingleSignOnProps {
  verificationService: VerificationService;
  stepName: VerificationStepsEnum.sso | VerificationStepsEnum.thirdPartyLoop;
  messages: SingleSignOnMessages;
  cancelButtonType?: ButtonType;
  isReclaim?: boolean;
}

const SingleSignOnComponent = ({
  verificationService,
  stepName,
  messages,
  cancelButtonType = ButtonType.PRIMARY,
  isReclaim = false,
}: SingleSignOnProps) => {
  const { programId } = verificationService;
  const viewModel = verificationService.viewModel as SingleSignOnViewModel;
  const previousViewModel =
    (verificationService.previousViewModel as StudentPersonalInfoViewModel) || undefined;
  const verificationResponse = verificationService.verificationResponse as SSOResponse;
  const previousVerificationResponse =
    verificationService.previousVerificationResponse || undefined;
  const { verificationId, locale } = verificationService.verificationResponse;
  const isMockingStep = getRouteOverride();
  const dashedStepName = dashed(stepName);
  let timeout;

  const logo = (
    <LogoComponent
      verificationService={verificationService}
      fallbackLogo={
        <img
          className={`sid-${dashedStepName}-logo-default sid-l-lead-image`}
          alt=""
          src="https://assets-resources.sheerid.com/common/images/2018/icons/doc-pending.svg"
        />
      }
    />
  );

  const pollForVerificationServiceUpdates = () =>
    setTimeout(() => {
      verificationService.fetchExistingVerificationRequest(
        programId,
        verificationId,
        previousVerificationResponse,
        previousViewModel,
      );
    }, 1000);

  const updateViewModel = (key, value) => {
    const nextState = produce(viewModel, (draft) => {
      (draft[key] as any) = value;
    });
    verificationService.updateViewModel(nextState);
  };

  const cancelSingleSignOnStep = () => {
    recordCTAClickEvent(EventName.SSO_CANCEL_CLICKED, {
      programId,
      locale,
      verificationId,
    });

    closeTabRef();
    const nextState = produce(viewModel, () => {});
    logger.info("SingleSignOnComponent submitting cancel request");
    clearTimeout(timeout);
    verificationService.submitStep(stepName, nextState, verificationService.verificationResponse);
  };

  const updateViewModelAndOpenLoginTab = (key, value) => {
    recordCTAClickEvent(EventName.SSO_SIGN_IN_CLICKED, {
      programId,
      locale,
      verificationId,
    });

    updateViewModel(key, value);
    openLoginTab();
  };

  const openLoginTab = () => {
    setTabRef(window.open(verificationResponse.loginUrl, "_blank"));
    getTabRef().focus();
  };

  if (viewModel.isSingleSignOnActive && !isMockingStep) {
    timeout = pollForVerificationServiceUpdates();
  }

  if (!viewModel.countryChoice && previousViewModel && previousViewModel.countryChoice) {
    updateViewModel("countryChoice", previousViewModel.countryChoice);
  }

  return (
    <div
      id={`sid-step-${dashedStepName}`}
      className={`sid-${dashedStepName}-container sid-l-container`}
      data-testid={`sid-step-${dashedStepName}`}
    >
      <div role="main">
        {viewModel.isSingleSignOnActive ? (
          <SingleSignOnPendingComponent
            isReclaim={isReclaim}
            stepName={stepName}
            messages={messages}
            logo={logo}
            cancelSingleSignOn={cancelSingleSignOnStep}
            cancelButtonType={cancelButtonType}
          />
        ) : (
          <>
            {isReclaim ? (
              <ReclaimContentComponent
                verificationService={verificationService}
                messages={messages}
                updateViewModelAndOpenLoginTab={updateViewModelAndOpenLoginTab}
                dashedStepName={dashedStepName}
              />
            ) : (
              <SSOContentComponent
                verificationService={verificationService}
                messages={messages}
                updateViewModelAndOpenLoginTab={updateViewModelAndOpenLoginTab}
                dashedStepName={dashedStepName}
              />
            )}
          </>
        )}
      </div>
      {!isReclaim && <FormFooter verificationService={verificationService} />}
    </div>
  );
};

export { SingleSignOnComponent };
